/*
 * Styled dropdown for the configurators' select fields
 * (.product-express__field .select). The browser's own popup can't be
 * styled, so the list is drawn in the page's style, the full width of the
 * field, and opens as part of it.
 *
 * The native <select> stays in the form as the source of truth: picking an
 * option sets its value and fires a bubbling "change", so variant-selects,
 * product-express.js and the cake configurators keep working unchanged.
 *
 * Keyboard: Enter / Space / arrows open; arrows, Home, End and typing a
 * character move; Enter / Space pick; Escape and Tab close.
 */
(function () {
  if (window.__customSelectReady) return;
  window.__customSelectReady = true;

  let uid = 0;

  function labelFor(select) {
    if (select.getAttribute('aria-label')) return select.getAttribute('aria-label');
    if (select.id) {
      const label = document.querySelector('label[for="' + CSS.escape(select.id) + '"]');
      if (label) return label.textContent.replace(/\*/g, '').trim();
    }
    const legend = select.closest('fieldset') && select.closest('fieldset').querySelector('legend');
    return legend ? legend.textContent.trim() : '';
  }

  function enhance(wrapper) {
    const select = wrapper.querySelector('select.select__select');
    if (!select || wrapper.dataset.customSelect === 'ready') return;
    wrapper.dataset.customSelect = 'ready';
    wrapper.classList.add('custom-select');

    const id = 'CustomSelect-' + ++uid;
    const labelText = labelFor(select);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'custom-select__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', id + '-list');
    const valueEl = document.createElement('span');
    valueEl.className = 'custom-select__value';
    button.appendChild(valueEl);

    const list = document.createElement('ul');
    list.className = 'custom-select__list';
    list.id = id + '-list';
    list.setAttribute('role', 'listbox');
    if (labelText) list.setAttribute('aria-label', labelText);
    list.tabIndex = -1;
    list.hidden = true;

    select.classList.add('custom-select__native');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    select.after(button);
    wrapper.appendChild(list);

    let activeIndex = -1;

    function optionEls() {
      return Array.from(list.children);
    }

    function build() {
      list.textContent = '';
      Array.from(select.options).forEach((option, index) => {
        const item = document.createElement('li');
        item.className = 'custom-select__option';
        item.id = id + '-option-' + index;
        item.setAttribute('role', 'option');
        item.dataset.index = index;
        item.textContent = option.textContent.trim();
        if (option.disabled) item.setAttribute('aria-disabled', 'true');
        list.appendChild(item);
      });
      sync();
    }

    function sync() {
      const selected = select.options[select.selectedIndex];
      const text = selected ? selected.textContent.trim() : '';
      valueEl.textContent = text;
      button.setAttribute('aria-label', labelText ? labelText + ': ' + text : text);
      optionEls().forEach((item, index) => {
        item.setAttribute('aria-selected', index === select.selectedIndex ? 'true' : 'false');
      });
    }

    function isOpen() {
      return !list.hidden;
    }

    function setActive(index) {
      const items = optionEls();
      if (!items.length) return;
      const clamped = Math.max(0, Math.min(items.length - 1, index));
      items.forEach((item, i) => item.classList.toggle('is-active', i === clamped));
      activeIndex = clamped;
      const active = items[clamped];
      list.setAttribute('aria-activedescendant', active.id);
      // Keep the active option in view inside the list without scrolling the page.
      if (active.offsetTop < list.scrollTop) {
        list.scrollTop = active.offsetTop;
      } else if (active.offsetTop + active.offsetHeight > list.scrollTop + list.clientHeight) {
        list.scrollTop = active.offsetTop + active.offsetHeight - list.clientHeight;
      }
    }

    function moveActive(step) {
      const items = optionEls();
      let next = activeIndex;
      for (let i = 0; i < items.length; i++) {
        next = Math.max(0, Math.min(items.length - 1, next + step));
        if (items[next].getAttribute('aria-disabled') !== 'true') break;
      }
      setActive(next);
    }

    function open() {
      if (isOpen()) return;
      list.hidden = false;
      wrapper.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');

      // Open upwards when there isn't room below but there is above.
      wrapper.classList.remove('custom-select--up');
      const rect = wrapper.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < list.offsetHeight && rect.top > spaceBelow) {
        wrapper.classList.add('custom-select--up');
      }

      setActive(Math.max(0, select.selectedIndex));
      list.focus({ preventScroll: true });
      document.addEventListener('pointerdown', onOutsidePointer, true);
    }

    function close(returnFocus) {
      if (!isOpen()) return;
      list.hidden = true;
      wrapper.classList.remove('is-open', 'custom-select--up');
      button.setAttribute('aria-expanded', 'false');
      list.removeAttribute('aria-activedescendant');
      document.removeEventListener('pointerdown', onOutsidePointer, true);
      if (returnFocus) button.focus();
    }

    function choose(index) {
      const option = select.options[index];
      if (!option || option.disabled) return;
      if (select.selectedIndex !== index) {
        select.selectedIndex = index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      close(true);
    }

    function onOutsidePointer(event) {
      if (!wrapper.contains(event.target)) close(false);
    }

    button.addEventListener('click', () => {
      if (isOpen()) {
        close(true);
      } else {
        open();
      }
    });

    button.addEventListener('keydown', (event) => {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        open();
      }
    });

    list.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveActive(-1);
          break;
        case 'Home':
          event.preventDefault();
          setActive(0);
          break;
        case 'End':
          event.preventDefault();
          setActive(optionEls().length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          choose(activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          close(true);
          break;
        case 'Tab':
          close(false);
          break;
        default:
          if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const key = event.key.toLowerCase();
            const items = optionEls();
            for (let i = 1; i <= items.length; i++) {
              const index = (activeIndex + i) % items.length;
              if (items[index].textContent.toLowerCase().startsWith(key)) {
                setActive(index);
                break;
              }
            }
          }
      }
    });

    list.addEventListener('click', (event) => {
      const item = event.target.closest('.custom-select__option');
      if (item) choose(parseInt(item.dataset.index, 10));
    });

    list.addEventListener('pointermove', (event) => {
      const item = event.target.closest('.custom-select__option');
      if (item && item.getAttribute('aria-disabled') !== 'true') {
        const index = parseInt(item.dataset.index, 10);
        if (index !== activeIndex) setActive(index);
      }
    });

    wrapper.addEventListener('focusout', (event) => {
      if (!wrapper.contains(event.relatedTarget)) close(false);
    });

    // A <label for> still points at the native select; send its focus on.
    select.addEventListener('focus', () => button.focus());
    select.addEventListener('change', sync);

    // Dawn's variant-selects rewrites the options' "selected" attributes, and
    // other code may swap the options; keep the list in step.
    new MutationObserver(() => {
      if (isOpen()) {
        sync();
      } else {
        build();
      }
    }).observe(select, { childList: true, subtree: true, attributes: true, attributeFilter: ['selected', 'disabled'] });

    build();
  }

  function init(root) {
    root.querySelectorAll('.product-express__field .select').forEach(enhance);
  }

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
